import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import en from './locales/en.json';
import ur from './locales/ur.json';
import ar from './locales/ar.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import it from './locales/it.json';
import pt from './locales/pt.json';
import ru from './locales/ru.json';
import zh from './locales/zh.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import hi from './locales/hi.json';
import tr from './locales/tr.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import th from './locales/th.json';
import id from './locales/id.json';
import vi from './locales/vi.json';
import ms from './locales/ms.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';

// Country to language mapping (comprehensive)
export const COUNTRY_TO_LANGUAGE = {
  // English speaking
  'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en', 'ZA': 'en', 'SG': 'en',
  'PH': 'en', 'NG': 'en', 'KE': 'en', 'GH': 'en', 'ZW': 'en', 'JM': 'en',
  // Spanish speaking
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es', 'VE': 'es', 'CL': 'es', 
  'EC': 'es', 'GT': 'es', 'CU': 'es', 'BO': 'es', 'DO': 'es', 'HN': 'es', 'PY': 'es',
  'SV': 'es', 'NI': 'es', 'CR': 'es', 'PA': 'es', 'UY': 'es', 'PR': 'es',
  // French speaking
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'CA': 'fr', 'SN': 'fr', 'CI': 'fr', 'CM': 'fr',
  'MG': 'fr', 'ML': 'fr', 'BF': 'fr', 'NE': 'fr', 'TG': 'fr', 'BJ': 'fr', 'CD': 'fr',
  // German speaking
  'DE': 'de', 'AT': 'de', 'LI': 'de', 'LU': 'de',
  // Italian speaking
  'IT': 'it', 'SM': 'it', 'VA': 'it',
  // Portuguese speaking
  'PT': 'pt', 'BR': 'pt', 'AO': 'pt', 'MZ': 'pt',
  // Russian speaking
  'RU': 'ru', 'BY': 'ru', 'KZ': 'ru', 'KG': 'ru',
  // Chinese speaking
  'CN': 'zh', 'TW': 'zh', 'HK': 'zh', 'MO': 'zh',
  // Japanese
  'JP': 'ja',
  // Korean
  'KR': 'ko',
  // Hindi speaking
  'IN': 'hi',
  // Urdu speaking
  'PK': 'ur',
  // Arabic speaking
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'QA': 'ar', 'KW': 'ar', 'BH': 'ar', 
  'OM': 'ar', 'JO': 'ar', 'LB': 'ar', 'IQ': 'ar', 'SY': 'ar', 'YE': 'ar',
  'LY': 'ar', 'TN': 'ar', 'DZ': 'ar', 'MA': 'ar', 'SD': 'ar', 'PS': 'ar',
  // Turkish
  'TR': 'tr', 'CY': 'tr',
  // Dutch speaking
  'NL': 'nl', 'SR': 'nl',
  // Polish
  'PL': 'pl',
  // Thai
  'TH': 'th',
  // Indonesian
  'ID': 'id',
  // Vietnamese
  'VN': 'vi',
  // Malay
  'MY': 'ms', 'BN': 'ms',
  // Finnish
  'FI': 'fi',
  // Swedish
  'SE': 'sv',
  // Norwegian (fallback to Swedish for now)
  'NO': 'sv',
  // Danish (fallback to Swedish for now)
  'DK': 'sv'
};

// All supported languages with info
export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', dir: 'ltr', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr', flag: '🇧🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', dir: 'ltr', flag: '🇰🇷' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', dir: 'rtl', flag: '🇵🇰' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr', flag: '🇹🇷' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', dir: 'ltr', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', dir: 'ltr', flag: '🇵🇱' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', dir: 'ltr', flag: '🇹🇭' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', dir: 'ltr', flag: '🇮🇩' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', dir: 'ltr', flag: '🇻🇳' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', dir: 'ltr', flag: '🇲🇾' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', dir: 'ltr', flag: '🇫🇮' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', dir: 'ltr', flag: '🇸🇪' }
];

// Get language for country
export const getLanguageForCountry = (countryCode) => {
  return COUNTRY_TO_LANGUAGE[countryCode] || 'en';
};

// Get language info
export const getLanguageInfo = (langCode) => {
  return LANGUAGES.find(l => l.code === langCode) || LANGUAGES[0];
};

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ur: { translation: ur },
      ar: { translation: ar },
      es: { translation: es },
      fr: { translation: fr },
      de: { translation: de },
      it: { translation: it },
      pt: { translation: pt },
      ru: { translation: ru },
      zh: { translation: zh },
      ja: { translation: ja },
      ko: { translation: ko },
      hi: { translation: hi },
      tr: { translation: tr },
      nl: { translation: nl },
      pl: { translation: pl },
      th: { translation: th },
      id: { translation: id },
      vi: { translation: vi },
      ms: { translation: ms },
      fi: { translation: fi },
      sv: { translation: sv }
    },
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng'
    }
  });

// Function to change language and update direction
export const changeLanguage = (langCode) => {
  const langInfo = getLanguageInfo(langCode);
  i18n.changeLanguage(langCode);
  
  // Update document direction for RTL languages
  document.documentElement.dir = langInfo.dir;
  document.documentElement.lang = langCode;
  
  // Store in localStorage
  localStorage.setItem('i18nextLng', langCode);
  localStorage.setItem('languageDir', langInfo.dir);
  
  return langInfo;
};

// Set initial direction on load
const currentLang = i18n.language || 'en';
const langInfo = getLanguageInfo(currentLang);
document.documentElement.dir = langInfo.dir;
document.documentElement.lang = currentLang;

export default i18n;
